// Wave 20.49 — Privacy zones settings page.
//
// Rider creates named circular zones (home, work, gym) and any
// polyline point inside a zone gets stripped from responses that
// reach anyone but the owner (feed cards, drop links, replay
// share). Zone data ships to /users/me/privacy-zones on the API.

import { fmtDistance, useUnitSystem } from './services/useUnitSystem';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import mapboxgl from 'mapbox-gl';
import { attachMinimalAttribution } from './services/mapAttribution';
import 'mapbox-gl/dist/mapbox-gl.css';
import PageLayout from './components/PageLayout';
import { colors } from './theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';
const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
  'pk.eyJ1IjoiYWVyby1jaGF0IiwiYSI6ImNtY3J4bnp0MTBjeTYybm42bzVjbWtsdmkifQ.rbgswhST93lMRY-CzQZ05Q';

mapboxgl.accessToken = MAPBOX_TOKEN;

function auth(): Record<string, string> {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

interface Zone {
  id: number;
  name: string;
  lat: number;
  lng: number;
  radiusM: number;
  createdAt: string | null;
}

// Draw a `radius_m` circle on a mapbox-gl instance as a filled
// polygon (mapbox has no first-class circle; we approximate with a
// 64-side polygon in metric coordinates).
function circlePolygon(lat: number, lng: number, radiusM: number): GeoJSON.Feature<GeoJSON.Polygon> {
  const points = 64;
  const coords: [number, number][] = [];
  const km = radiusM / 1000;
  const kmPerDegLat = 110.574;
  const kmPerDegLng = 111.32 * Math.cos((lat * Math.PI) / 180);
  for (let i = 0; i < points; i++) {
    const theta = (i / points) * 2 * Math.PI;
    const dLat = (km / kmPerDegLat) * Math.cos(theta);
    const dLng = (km / kmPerDegLng) * Math.sin(theta);
    coords.push([lng + dLng, lat + dLat]);
  }
  coords.push(coords[0]);
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [coords] },
  };
}

// Small read-only preview map for the zone list. Renders the
// circle centered in the frame.
function ZonePreviewMap({ lat, lng, radiusM }: { lat: number; lng: number; radiusM: number }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    const map = new mapboxgl.Map({
      container: ref.current,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: [lng, lat],
      zoom: Math.max(12, 16 - Math.log2(Math.max(1, radiusM / 100))),
      interactive: false,
      attributionControl: false,
      logoPosition: 'bottom-right',
      // Wave 20.49bl — no tile crossfade animation on mount so
      // the map doesn't visually fly-in as tiles load.
      fadeDuration: 0,
    });
    mapRef.current = map;
    attachMinimalAttribution(map);
    map.on('load', () => {
      map.addSource('zone', { type: 'geojson', data: circlePolygon(lat, lng, radiusM) });
      map.addLayer({
        id: 'zone-fill',
        type: 'fill',
        source: 'zone',
        paint: { 'fill-color': colors.brand, 'fill-opacity': 0.25 },
      });
      map.addLayer({
        id: 'zone-outline',
        type: 'line',
        source: 'zone',
        paint: { 'line-color': colors.brand, 'line-width': 2 },
      });
    });
    return () => {
      try {
        map.remove();
      } catch {
        /* ignore */
      }
      mapRef.current = null;
    };
  }, [lat, lng, radiusM]);
  return <Box ref={ref} sx={{ width: '100%', height: 140, borderRadius: 1.5, overflow: 'hidden' }} />;
}

// Interactive picker: click the map to place / move the center pin.
function ZonePicker({
  lat,
  lng,
  radiusM,
  onChange,
}: {
  lat: number;
  lng: number;
  radiusM: number;
  onChange: (next: { lat: number; lng: number }) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    const map = new mapboxgl.Map({
      container: ref.current,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: [lng, lat],
      zoom: 14,
      attributionControl: false,
      logoPosition: 'bottom-right',
      // Wave 20.49bl — no tile crossfade on mount.
      fadeDuration: 0,
    });
    mapRef.current = map;
    attachMinimalAttribution(map);
    map.on('load', () => {
      map.addSource('zone', { type: 'geojson', data: circlePolygon(lat, lng, radiusM) });
      map.addLayer({
        id: 'zone-fill',
        type: 'fill',
        source: 'zone',
        paint: { 'fill-color': colors.brand, 'fill-opacity': 0.25 },
      });
      map.addLayer({
        id: 'zone-outline',
        type: 'line',
        source: 'zone',
        paint: { 'line-color': colors.brand, 'line-width': 2 },
      });
      markerRef.current = new mapboxgl.Marker({ color: colors.brand, draggable: true })
        .setLngLat([lng, lat])
        .addTo(map);
      markerRef.current.on('dragend', () => {
        const p = markerRef.current!.getLngLat();
        onChange({ lat: p.lat, lng: p.lng });
      });
      map.on('click', (e) => {
        markerRef.current?.setLngLat(e.lngLat);
        onChange({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      });
    });
    return () => {
      try {
        map.remove();
      } catch {
        /* ignore */
      }
      mapRef.current = null;
      markerRef.current = null;
    };
    // Deliberately mount-only — subsequent lat/lng/radius changes go
    // through the imperative update effect below so the map doesn't
    // tear down on every slider tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redraw the circle whenever lat/lng/radius changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const src = map.getSource('zone') as mapboxgl.GeoJSONSource | undefined;
    if (src) src.setData(circlePolygon(lat, lng, radiusM));
    markerRef.current?.setLngLat([lng, lat]);
  }, [lat, lng, radiusM]);

  return <Box ref={ref} sx={{ width: '100%', height: 320, borderRadius: 1.5, overflow: 'hidden' }} />;
}

export default function PrivacyZones() {
  const units = useUnitSystem();
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // Wave 20.49b — edit an existing zone. When set, opens the same
  // editor dialog pre-filled with that zone's values; saves via PATCH.
  const [editing, setEditing] = useState<Zone | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/users/me/privacy-zones`, { headers: auth() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const rows: Zone[] = await res.json();
      setZones(rows);
    } catch (e: any) {
      setError('Could not load privacy zones.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onDelete = async (id: number) => {
    try {
      const res = await fetch(`${API}/users/me/privacy-zones/${id}`, {
        method: 'DELETE',
        headers: auth(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setZones((prev) => prev.filter((z) => z.id !== id));
    } catch {
      setError('Could not delete zone.');
    }
  };

  return (
    <PageLayout maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="overline" sx={{ color: colors.brand }}>
          PRIVACY
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, letterSpacing: '-0.02em' }}>
          Privacy zones
        </Typography>
        <Typography sx={{ color: colors.textMuted, mt: 1, maxWidth: 640 }}>
          Draw a circle around home, work, or anywhere else you'd rather not
          expose in a shared route. Any point inside a zone is stripped from
          the polyline everywhere except your own session detail — the feed,
          drop links, and replay shares all see the trimmed route.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <Button
          onClick={() => setAddOpen(true)}
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          sx={{ backgroundColor: colors.brand, color: '#0A0E1A', fontWeight: 700 }}
        >
          Add a zone
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: colors.brand }} />
        </Box>
      ) : zones.length === 0 ? (
        <Box
          sx={{
            p: 4,
            borderRadius: 3,
            border: `1px dashed ${colors.border}`,
            textAlign: 'center',
            color: colors.textMuted,
          }}
        >
          You haven't added any privacy zones yet.
        </Box>
      ) : (
        <Stack spacing={2}>
          {zones.map((z) => (
            <Box
              key={z.id}
              sx={{
                p: 2,
                borderRadius: 3,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box sx={{ flex: '0 0 220px' }}>
                  <ZonePreviewMap lat={z.lat} lng={z.lng} radiusM={z.radiusM} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, color: colors.text, fontSize: '1.1rem' }}>
                        {z.name}
                      </Typography>
                      <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mt: 0.5 }}>
                        {z.lat.toFixed(5)}, {z.lng.toFixed(5)}
                      </Typography>
                      <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                        Radius: {fmtDistance(z.radiusM, units)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => setEditing(z)}
                        sx={{ color: colors.textMuted, '&:hover': { color: colors.brand } }}
                        aria-label="Edit zone"
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onDelete(z.id)}
                        sx={{ color: colors.textMuted, '&:hover': { color: colors.danger } }}
                        aria-label="Delete zone"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {addOpen && (
        <ZoneEditorDialog
          initial={null}
          onClose={() => setAddOpen(false)}
          onSaved={(z) => {
            setZones((prev) => [...prev, z]);
            setAddOpen(false);
          }}
        />
      )}
      {editing && (
        <ZoneEditorDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={(z) => {
            setZones((prev) => prev.map((r) => (r.id === z.id ? z : r)));
            setEditing(null);
          }}
        />
      )}
    </PageLayout>
  );
}

function ZoneEditorDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: Zone | null;
  onClose: () => void;
  onSaved: (z: Zone) => void;
}) {
  const units = useUnitSystem();
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? 'Home');
  const [radiusM, setRadiusM] = useState(initial?.radiusM ?? 300);
  const [center, setCenter] = useState<{ lat: number; lng: number }>(
    initial
      ? { lat: initial.lat, lng: initial.lng }
      : { lat: 40.75, lng: -73.99 },
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // In edit mode we already have coordinates — skip geolocation.
  const [locating, setLocating] = useState(!isEdit);

  useEffect(() => {
    if (isEdit) return;
    if (!navigator.geolocation) {
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
    );
  }, [isEdit]);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const body = JSON.stringify({
        name,
        lat: center.lat,
        lng: center.lng,
        radiusM,
      });
      const url = isEdit
        ? `${API}/users/me/privacy-zones/${initial!.id}`
        : `${API}/users/me/privacy-zones`;
      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body,
      });
      if (!res.ok) {
        const rb = await res.json().catch(() => ({}));
        throw new Error(rb?.message || `HTTP ${res.status}`);
      }
      const z: Zone = await res.json();
      onSaved(z);
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const memoCenter = useMemo(() => center, [center.lat, center.lng]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        {isEdit ? 'Edit privacy zone' : 'New privacy zone'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 64))}
            fullWidth
            size="small"
            inputProps={{ maxLength: 64 }}
          />
          {locating ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} sx={{ color: colors.brand }} />
              <Typography sx={{ ml: 2, color: colors.textMuted, alignSelf: 'center' }}>
                Locating…
              </Typography>
            </Box>
          ) : (
            <ZonePicker
              lat={memoCenter.lat}
              lng={memoCenter.lng}
              radiusM={radiusM}
              onChange={setCenter}
            />
          )}
          <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
            Tap the map or drag the pin to place the center.
          </Typography>
          <Box>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>
              Radius: {fmtDistance(radiusM, units)}
            </Typography>
            <Slider
              value={radiusM}
              min={50}
              max={2000}
              step={50}
              onChange={(_, v) => setRadiusM(Array.isArray(v) ? v[0] : v)}
              sx={{ color: colors.brand }}
            />
          </Box>
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ color: colors.textMuted }}>
          Cancel
        </Button>
        <Button
          onClick={save}
          disabled={saving || !name.trim() || locating}
          variant="contained"
          sx={{ backgroundColor: colors.brand, color: '#0A0E1A', fontWeight: 700 }}
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save zone'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
